/**
 * Phase schedules for sim-mode flows.
 *
 * Each schedule is an ordered list of `{phase, time-from-start-ms}`
 * tuples that drive a {@link useSimulatedTransaction} timer. The
 * widget reaches each phase in turn until either the happy or error
 * terminus.
 *
 * These constants were extracted verbatim from the prior
 * `TradeWidgetShowcase.tsx` forks so the new sim flow plays at
 * exactly the same cadence as the QA tool that designers signed off
 * on. The forked Flow components themselves are deleted in the
 * showcase-replacement commit.
 */

import type { SimulatedPhase } from "./types";

/** Single step in a phase schedule: arrive at `p` at `t` ms from execute(). */
export interface ScheduleStep {
    p: SimulatedPhase;
    t: number;
}

/** Happy + error pair for a single widget. */
export interface ScheduleSet {
    happy: ScheduleStep[];
    error: ScheduleStep[];
}

// -- Buy widget ----------------------------------------------------

/**
 * Buy flow with the full postSuccess fade-in (used inside the
 * SwapStake widget). Reaches `postSuccess` so the EarnsCard reveal
 * animation plays after a successful buy.
 */
export const BUY_SCHEDULE: ScheduleSet = {
    happy: [
        { p: "focused", t: 700 },
        { p: "filled", t: 2000 },
        { p: "pending", t: 3500 },
        { p: "receipt", t: 4300 },
        { p: "processing", t: 5800 },
        { p: "success", t: 7300 },
        { p: "postSuccess", t: 8800 }
    ],
    error: [
        { p: "focused", t: 700 },
        { p: "filled", t: 2000 },
        { p: "pending", t: 3500 },
        { p: "receipt", t: 4300 },
        { p: "processing", t: 5800 },
        { p: "error", t: 7300 }
    ]
};

/**
 * Landing-page buy flow (slower; longer focused dwell before filled
 * so reviewers can see the empty → focused → filled keystroke arc).
 */
export const LANDING_BUY_SCHEDULE: ScheduleSet = {
    happy: [
        { p: "focused", t: 1200 },
        { p: "filled", t: 3000 },
        { p: "pending", t: 4500 },
        { p: "receipt", t: 5300 },
        { p: "processing", t: 6800 },
        { p: "success", t: 8300 }
    ],
    error: [
        { p: "focused", t: 1200 },
        { p: "filled", t: 3000 },
        { p: "pending", t: 4500 },
        { p: "receipt", t: 5300 },
        { p: "processing", t: 6800 },
        { p: "error", t: 8300 }
    ]
};

// -- Sell widget ---------------------------------------------------

/**
 * Sell flow. Same cadence as Buy minus the postSuccess animation
 * (no EarnsCard on the Sell side).
 */
export const SELL_SCHEDULE: ScheduleSet = {
    happy: [
        { p: "focused", t: 700 },
        { p: "filled", t: 2000 },
        { p: "pending", t: 3500 },
        { p: "receipt", t: 4300 },
        { p: "processing", t: 5800 },
        { p: "success", t: 7300 }
    ],
    error: [
        { p: "focused", t: 700 },
        { p: "filled", t: 2000 },
        { p: "pending", t: 3500 },
        { p: "receipt", t: 4300 },
        { p: "processing", t: 5800 },
        { p: "error", t: 7300 }
    ]
};

// -- Stake/Unstake/Withdraw widgets ----------------------------------

/**
 * Stake / Unstake / Withdraw share a schedule. No `receipt` phase
 * (the stake forms don't render a separate receipt step); pending
 * goes directly to processing.
 */
export const STAKE_SCHEDULE: ScheduleSet = {
    happy: [
        { p: "filled", t: 1500 },
        { p: "pending", t: 3000 },
        { p: "processing", t: 4500 },
        { p: "success", t: 6000 }
    ],
    error: [
        { p: "filled", t: 1500 },
        { p: "pending", t: 3000 },
        { p: "processing", t: 4500 },
        { p: "error", t: 6000 }
    ]
};

export const UNSTAKE_SCHEDULE: ScheduleSet = STAKE_SCHEDULE;
export const WITHDRAW_SCHEDULE: ScheduleSet = STAKE_SCHEDULE;

// -- Mock receipt rows ---------------------------------------------

export const MOCK_BUY_RECEIPT_ROWS = [
    { label: "Price", value: "10,000.00 USDC" },
    { label: "Price Impact", value: "0.00%" },
    { label: "Fee", value: "$0.00" },
    { label: "Network Cost", value: "0.000005 SOL" }
] as const;

export const MOCK_SELL_RECEIPT_ROWS = [
    { label: "Price", value: "10,000.00 USDv" },
    { label: "Price Impact", value: "0.00%" },
    { label: "Fee", value: "$0.00" },
    { label: "Network Cost", value: "0.000005 SOL" }
] as const;

export const MOCK_STAKE_RECEIPT_ROWS = [{ label: "Network Cost", value: "0.000005 SOL" }] as const;

export const MOCK_UNSTAKE_RECEIPT_ROWS = [
    { label: "Unstake", value: "500.00 sUSDv" },
    { label: "Network Cost", value: "0.000005 SOL" }
] as const;

export const MOCK_WITHDRAW_RECEIPT_ROWS = [
    { label: "Withdraw", value: "500.00 USDv" },
    { label: "Network Cost", value: "0.000005 SOL" }
] as const;
